<template>
	<!-- Rendering is strictly selection-driven: only curricula present in
	     selectedGoals get rendered; the school curriculum prop only orders them.
	     0 groups: render nothing (the host's empty-state text shows). -->
	<template v-if="goalGroups.length === 1">
		<GoalPickerExternal
			v-if="goalGroups[0].curriculum.hasInlineGoalViewer()"
			mode="inline"
			:curriculum="goalGroups[0].curriculum"
			:context="context"
			:selectedGoals="selectedGoals"
			:suggestedGoals="suggestedGoals"
			@goalSelected="$emit('goalSelected', $event)"
			@goalUnselected="$emit('goalUnselected', $event)"
		/>
		<SelectedGoalsList
			v-else
			:goals="goalGroups[0].goals"
			@goalUnselected="$emit('goalUnselected', $event)"
			@goalMuteToggled="$emit('goalMuteToggled', $event)"
		/>
	</template>

	<div v-else-if="goalGroups.length > 1" class="flex flex-col gap-2 py-4">
		<div
			v-for="group in goalGroups"
			:key="group.key"
			class="collapse collapse-arrow border border-base-200 rounded-box"
		>
			<input
				type="radio"
				:name="accordionName"
				:checked="group.key === openGroupKey"
				@change="openGroupKey = group.key"
			/>
			<div class="collapse-title font-semibold flex items-center gap-2">
				{{ group.curriculum.name || $t('Other') }}
				<div class="badge badge-sm badge-info">{{ countParentGoals(group.goals) }}</div>
			</div>
			<div class="collapse-content">
				<!-- The inline viewer gets the FULL selection: it filters to its own
				     source internally and needs the complete list to stay in sync. -->
				<GoalPickerExternal
					v-if="group.curriculum.hasInlineGoalViewer()"
					mode="inline"
					:curriculum="group.curriculum"
					:context="context"
					:selectedGoals="selectedGoals"
					:suggestedGoals="suggestedGoals"
					@goalSelected="$emit('goalSelected', $event)"
					@goalUnselected="$emit('goalUnselected', $event)"
				/>
				<SelectedGoalsList
					v-else
					:goals="group.goals"
					@goalUnselected="$emit('goalUnselected', $event)"
					@goalMuteToggled="$emit('goalMuteToggled', $event)"
				/>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import GoalPickerExternal from "@/components/GoalPickerExternal.vue";
import SelectedGoalsList from "@/components/SelectedGoalsList.vue";
import { Curriculum, CurriculumGoalGroup, countParentGoals } from "@/models/Curriculum.model";
import { Goal } from "@/models/Goal.model";

export default {

	components: {
		GoalPickerExternal,
		SelectedGoalsList,
	},

	emits: ['goalSelected', 'goalUnselected', 'goalMuteToggled'],

	props: {
		curriculum: {
			type: Curriculum,
			default: null,
		},

		selectedGoals: {
			type: Array,
			default: () => [],
		},

		suggestedGoals: {
			type: Array,
			default: () => [],
		},

		context: {
			type: String,
			default: 'lessonPlan',
		},
	},

	data() {
		return {
			// Open accordion section, tracked by group key so removing the open
			// group's last goal falls back to the first section.
			openGroupKeyInternal: null as string | null,
			accordionName: 'goals-accordion-' + Math.floor(Math.random() * 999999),
		};
	},

	computed: {
		goalGroups(): CurriculumGoalGroup[] {
			// Ordering (school curriculum first, then registry order) is shared with the
			// read-only GoalsDisplay so both surfaces section a plan the same way.
			return Curriculum.groupGoals(this.selectedGoals as Goal[], this.curriculum);
		},

		openGroupKey: {
			get(): string | null {
				if (
					this.openGroupKeyInternal
					&& this.goalGroups.some((g: CurriculumGoalGroup) => g.key === this.openGroupKeyInternal)
				) {
					return this.openGroupKeyInternal;
				}
				return this.goalGroups[0]?.key ?? null;
			},
			set(value: string) {
				this.openGroupKeyInternal = value;
			},
		},
	},

	methods: {
		countParentGoals,
	},

};
</script>
